/**
 * This file includes polyfills needed by Angular and is loaded before the app.
 * You can add your own extra polyfills to this file.
 *
 * This file is divided into 2 sections:
 *   1. Browser polyfills. These are applied before loading ZoneJS and are sorted by browsers.
 *   2. Application imports. Files imported after ZoneJS that should be loaded before your main
 *      file.
 *
 * The current setup is for so-called "evergreen" browsers; the last versions of browsers that
 * automatically update themselves. This includes Safari >= 10, Chrome >= 55 (including Opera),
 * Edge >= 13 on the desktop, and iOS 10 and Chrome on mobile.
 *
 * Learn more in https://angular.io/guide/browser-support
 */

/***************************************************************************************************
 * BROWSER POLYFILLS
 */

/** IE9, IE10, IE11, and Chrome <55 requires all of the following polyfills.
 *  This also includes Android Emulators with older versions of Chrome and Google Search/Googlebot
 */

// import 'core-js/es6/symbol';
// import 'core-js/es6/object';
// import 'core-js/es6/function';
// import 'core-js/es6/parse-int';
// import 'core-js/es6/parse-float';
// import 'core-js/es6/number';
// import 'core-js/es6/math';
// import 'core-js/es6/string';
// import 'core-js/es6/date';
// import 'core-js/es6/array';
// import 'core-js/es6/regexp';
// import 'core-js/es6/map';
// import 'core-js/es6/weak-map';
// import 'core-js/es6/set';

/** IE10 and IE11 requires the following for NgClass support on SVG elements */
// import 'classlist.js';  // Run `npm install --save classlist.js`.

/** IE10 and IE11 requires the following for the Reflect API. */
// import 'core-js/es6/reflect';

/**
 * Web Animations `@angular/platform-browser/animations`
 * Only required if AnimationBuilder is used within the application and using IE/Edge or Safari.
 * Standard animation support in Angular DOES NOT require any polyfills (as of Angular 6.0).
 */
// import 'web-animations-js';  // Run `npm install --save web-animations-js`.

/**
 * By default, zone.js will patch all possible macroTask and DomEvents
 * user can disable parts of macroTask/DomEvents patch by setting following flags
 * because those flags need to be set before `zone.js` being loaded, and webpack
 * will put import in the top of bundle, so user need to create a separate file
 * in this directory (for example: zone-flags.ts), and put the following flags
 * into that file, and then add the following code before importing zone.js.
 * import './zone-flags.ts';
 *
 * The flags allowed in zone-flags.ts are listed here.
 *
 * The following flags will work for all browsers.
 *
 * (window as any).__Zone_disable_requestAnimationFrame = true; // disable patch requestAnimationFrame
 * (window as any).__Zone_disable_on_property = true; // disable patch onProperty such as onclick
 * (window as any).__zone_symbol__BLACK_LISTED_EVENTS = ['scroll', 'mousemove']; // disable patch specified eventNames
 *
 *  in IE/Edge developer tools, the addEventListener will also be wrapped by zone.js
 *  with the following flag, it will bypass `zone.js` patch for IE/Edge
 *
 *  (window as any).__Zone_enable_cross_context_check = true;
 *
 */

/***************************************************************************************************
 * Zone JS is required by default for Angular itself.
 */
import 'zone.js/dist/zone';  // Included with Angular CLI.

/**
 * Fix for "Zone.js has detected that ZoneAwarePromise `(window|global).Promise` has been
 * overwritten" (seen on Safari; never reproduced on Chrome).
 *
 * Root cause: `ng2-pdf-viewer` (added for the PDF preview feature) imports pdfjs-dist's ES5 build
 * (`pdfjs-dist/es5/build/pdf`), which runs this on module load:
 *
 *   (function checkPromise() {
 *     if (globalThis.Promise && globalThis.Promise.allSettled) { return; }
 *     globalThis.Promise = __w_pdfjs_require__(113); // a core-js Promise polyfill
 *   })();
 *
 * This zone.js version's `ZoneAwarePromise` (patched onto `window.Promise` two lines up) does NOT
 * implement `.allSettled` (added to the spec in ES2020, after this zone.js was written — confirmed
 * by grepping node_modules/zone.js/dist/zone.js for "allSettled": zero matches). So pdf.js's check
 * always fails and it unconditionally overwrites `window.Promise` with its own polyfill every time
 * its module runs (i.e. every time a document with a PDF preview is opened) — breaking Angular's
 * zone-based change detection for anything chained off a Promise from that point on.
 *
 * Fix: give `window.Promise` (== ZoneAwarePromise) a real `.allSettled` so pdf.js's feature check
 * passes and it never overwrites anything in the first place.
 *
 * NOTE — do NOT use core-js's `Promise.allSettled` module here (`core-js/features/promise/all-settled`
 * or the `es.promise`/`esnext.promise.all-settled` modules it pulls in): its own internal
 * Promise-correctness check treats `ZoneAwarePromise` as non-conforming (no `Symbol.species` support
 * in `.then()`, an ordinary trait for a custom Promise wrapper) and forcibly replaces `window.Promise`
 * wholesale with its own implementation as a result — reintroducing this exact bug in every browser,
 * not just Safari — so a hand-rolled `.allSettled` that only ADDS the missing method is the safe choice.
 *
 * Algorithm below matches the spec-accurate shape (same one core-js's own
 * `esnext.promise.all-settled.js` module implements — read directly, not imported, for exactly the
 * reason above): accepts any iterable via `Array.from`, and respects `this` for subclassing (calling
 * `SomePromiseSubclass.allSettled(...)` uses that subclass's own `.all`/`.resolve`, falling back to
 * `window.Promise` when called unbound, e.g. via a destructured reference). None of this touches or
 * replaces `window.Promise` itself — only adds the one missing static method to it.
 * 
 * NOTE - probably can be removed on Angular version higher then current (8.1.3) along with postinstall patching
 */
(function addPromiseAllSettledToZoneAwarePromise(): void {
  const target: any = (typeof window !== 'undefined') ? window : (typeof self !== 'undefined' ? self : null);
  if (!target || !target.Promise || typeof target.Promise.allSettled === 'function') { return; }

  target.Promise.allSettled = function (iterable: any): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const C: any = (this && typeof this.resolve === 'function' && typeof this.all === 'function') ? this : target.Promise;

    return C.all(
      Array.from(iterable, function (item: any) {
        return C.resolve(item).then(
          function (value: any) { return { status: 'fulfilled', value: value }; },
          function (reason: any) { return { status: 'rejected', reason: reason }; }
        );
      })
    );
  };
})();


/***************************************************************************************************
 * APPLICATION IMPORTS
 */
